import { useState, useLayoutEffect, type RefObject } from "react";

interface DOMRectObject {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
}
const initialBounds = Object.freeze({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
});

/**
 * `synchronous` (default `false`) reads the element's bounds directly in
 * `useLayoutEffect`, before the browser's first paint, instead of waiting on
 * `ResizeObserver`'s own callback — which is async even for its first
 * observation, so a consumer that renders differently for width `0` (the
 * initial state) than for the real width otherwise paints once at `0` first.
 * Off by default so every existing consumer of this hook (re-exported
 * publicly through `@sproutsocial/racine`) keeps today's exact behavior.
 */
export function useMeasure<TElement extends Element>(
  ref: RefObject<TElement>,
  synchronous = false
) {
  const [bounds, setContentRect] =
    useState<Readonly<DOMRectObject>>(initialBounds);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (synchronous) {
      const { x, y, width, height, top, right, bottom, left } =
        element.getBoundingClientRect();
      setContentRect({ x, y, width, height, top, right, bottom, left });
    }

    // in non-browser environments (e.g. Jest tests) ResizeObserver is not defined
    if (!("ResizeObserver" in window)) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { x, y, width, height, top, right, bottom, left } =
        entry.contentRect;
      setContentRect({
        x,
        y,
        width,
        height,
        top,
        right,
        bottom,
        left,
      });
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [ref, synchronous]);

  return bounds;
}
